import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type PriceType } from '@/api/refs';
import Drawer from '@/components/Drawer';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Довідник «Тип цін» (tenant-scoped, alembic 0260, 2026-06-08).
 *
 * Вид типу:
 * - Базовий — ціна вводиться вручну у закладці «Ціни» картки номенклатури.
 * - Динамічний — ціна обчислюється = ціна базового типу × (1 + наценка%/100).
 *   Обирається базовий тип-джерело + відсоток наценки.
 *
 * Дефолтні 3 базові типи (Закупівельна/Роздрібна/Оптова) — не видаляються.
 */
const SEED_CODES = new Set(['PURCHASE', 'RETAIL', 'WHOLESALE']);

interface FormState {
  id: string | null;
  name: string;
  currency_id: string;
  price_includes_vat: boolean;
  is_purchase: boolean;
  kind: 'BASE' | 'DYNAMIC';
  base_price_type_id: string;
  markup_percent: string;
}

const EMPTY = (uahId: string): FormState => ({
  id: null, name: '', currency_id: uahId, price_includes_vat: true,
  is_purchase: false, kind: 'BASE', base_price_type_id: '', markup_percent: '',
});

export default function PriceTypesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);

  const list = useQuery({ queryKey: ['price-types'], queryFn: refs.priceTypes.list });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies });

  const uahId = (currenciesQ.data ?? []).find((c) => c.code === 'UAH')?.id ?? '';
  const currCode = (id: string) => (currenciesQ.data ?? []).find((c) => c.id === id)?.code ?? '';
  const invalidate = () => qc.invalidateQueries({ queryKey: ['price-types'] });

  const rows = list.data ?? [];
  // Базові типи — джерела для динамічних (anti-chain: джерело має бути BASE).
  const baseTypes = useMemo(() => rows.filter((t) => t.kind === 'BASE'), [rows]);

  const save = useMutation({
    mutationFn: () => {
      const f = form!;
      const isDyn = f.kind === 'DYNAMIC';
      const payload = {
        name: f.name.trim(),
        currency_id: f.currency_id,
        price_includes_vat: f.price_includes_vat,
        is_purchase: f.is_purchase,
        kind: f.kind,
        base_price_type_id: isDyn ? f.base_price_type_id : null,
        markup_percent: isDyn ? f.markup_percent : null,
      };
      if (f.id) return refs.priceTypes.update(f.id, payload);
      return refs.priceTypes.create(payload);
    },
    onSuccess: () => { invalidate(); setForm(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.priceTypes.remove(id),
    onSuccess: invalidate,
  });

  const startEdit = (t: PriceType) => setForm({
    id: t.id, name: t.name, currency_id: t.currency_id,
    price_includes_vat: t.price_includes_vat, is_purchase: t.is_purchase,
    kind: t.kind, base_price_type_id: t.base_price_type_id ?? '',
    markup_percent: t.markup_percent ?? '',
  });

  const isDyn = form?.kind === 'DYNAMIC';
  const canSave = !!form?.name.trim() && !!form?.currency_id
    && (!isDyn || (!!form?.base_price_type_id && form?.markup_percent !== ''))
    && !save.isPending;

  const cols = useMemo<ColumnDef<PriceType>[]>(() => [
    {
      key: 'name', header: 'Назва', width: 250,
      sortValue: (t) => t.name.toLowerCase(),
      cell: (t) => {
        const seed = t.code != null && SEED_CODES.has(t.code);
        return <>{seed && <span title="Дефолтний тип">🔒 </span>}{t.name}</>;
      },
    },
    {
      key: 'kind', header: 'Вид', width: 130, align: 'center',
      sortValue: (t) => (t.kind === 'DYNAMIC' ? 'Динамічний' : 'Базовий'),
      cell: (t) => (t.kind === 'DYNAMIC' ? 'Динамічний' : 'Базовий'),
    },
    {
      key: 'currency', header: 'Валюта', width: 100, align: 'center',
      sortValue: (t) => currCode(t.currency_id),
      cell: (t) => currCode(t.currency_id),
    },
    {
      key: 'vat', header: 'З ПДВ', width: 90, align: 'center',
      sortValue: (t) => (t.price_includes_vat ? 1 : 0),
      cell: (t) => (t.price_includes_vat ? '✓' : ''),
    },
    {
      key: 'markup', header: 'Наценка', width: 110, align: 'center',
      sortValue: (t) => (t.kind === 'DYNAMIC' && t.markup_percent != null ? Number(t.markup_percent) : -1),
      cell: (t) => (t.kind === 'DYNAMIC' && t.markup_percent != null
        ? `${Number(t.markup_percent)}%` : '—'),
    },
    {
      key: 'actions', header: '', width: 110, align: 'right',
      cell: (t) => {
        const seed = t.code != null && SEED_CODES.has(t.code);
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <button title="Редагувати" onClick={() => startEdit(t)}>✎</button>
            {!seed && (
              <button className="danger" title="Видалити" style={{ marginLeft: '0.25rem' }}
                onClick={() => confirm(`Видалити тип «${t.name}»?`).then((ok) => { if (ok) remove.mutate(t.id); })}>✕</button>
            )}
          </span>
        );
      },
    },
  ], [currCode, confirm, remove]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Типи цін</h2>
        <button className="primary" onClick={() => setForm(EMPTY(uahId))}>+ Новий тип</button>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 720 }}>
        Тип ціни визначає, яку ціну застосовувати (наприклад, «Роздрібна»,
        «Оптова»). <strong>Базовий</strong> тип — ціна вводиться вручну.
        <strong> Динамічний</strong> — ціна рахується автоматично від базового
        плюс наценка (наприклад, «Роздрібна = Закупівельна + 30%»). Дефолтні
        типи позначені 🔒 — їх не можна видалити.
      </p>

      <DataTable<PriceType>
        rows={rows}
        columns={cols}
        rowKey={(t) => t.id}
        loading={list.isLoading}
        emptyText="Немає типів цін."
        initialSortKey="name"
      />

      {form && (
        <Drawer
          open={!!form}
          title={form.id ? 'Редагувати тип ціни' : 'Новий тип ціни'}
          onClose={() => setForm(null)}
          footer={
            <>
              <button onClick={() => setForm(null)}>Скасувати</button>
              <button className="primary" disabled={!canSave} onClick={() => save.mutate()}>
                Зберегти
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label>Назва
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
            </label>
            <label>Валюта
              <select value={form.currency_id} onChange={(e) => setForm({ ...form, currency_id: e.target.value })}>
                {(currenciesQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.price_includes_vat}
                onChange={(e) => setForm({ ...form, price_includes_vat: e.target.checked })} />
              Ціна включає ПДВ
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_purchase}
                onChange={(e) => setForm({ ...form, is_purchase: e.target.checked })} />
              Закупівельний тип
            </label>
            <label>Вид типу
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as 'BASE' | 'DYNAMIC' })}>
                <option value="BASE">Базовий (ціна вручну)</option>
                <option value="DYNAMIC">Динамічний (від базового + наценка)</option>
              </select>
            </label>
            {isDyn && (
              <>
                <label>Базовий тип-джерело
                  <select value={form.base_price_type_id}
                    onChange={(e) => setForm({ ...form, base_price_type_id: e.target.value })}>
                    <option value="">— оберіть базовий тип —</option>
                    {baseTypes
                      .filter((b) => b.id !== form.id && b.currency_id === form.currency_id)
                      .map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </label>
                <label>Наценка, %
                  <input type="number" step="0.01" value={form.markup_percent}
                    onChange={(e) => setForm({ ...form, markup_percent: e.target.value })} />
                </label>
              </>
            )}
          </div>
        </Drawer>
      )}
    </>
  );
}
